import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MessageSquare, Mail, Send } from 'lucide-react';

const LegalPageLayout = ({ title, children, onBack }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white">
    {/* Header */}
    <header className="fixed top-0 w-full z-50 bg-slate-900/90 backdrop-blur-md border-b border-cyan-300/30">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-2">
            <svg className="h-8 w-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
            </svg>
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              CyberGuard Global
            </span>
          </div>
          <button
            onClick={onBack}
            className="bg-cyan-400 hover:bg-opacity-80 text-gray-900 font-semibold px-6 py-2 rounded-lg transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </header>

    {/* Content */}
    <div className="pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
          {title}
        </h1>
        <div className="bg-gray-900/50 rounded-lg border border-cyan-300/20 p-8">
          <div className="prose prose-invert prose-cyan max-w-none">
            {children}
          </div>
        </div>

        {/* Need Assistance Section */}
        <div className="mt-8 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-300/30 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-3 flex items-center">
            <MessageSquare className="h-5 w-5 text-cyan-400 mr-2" />
            Questions About Our Policies?
          </h2>
          <p className="text-gray-300 mb-4 text-sm">
            Our team is here to help clarify any legal or privacy concerns.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Link
              to="/dashboard"
              className="flex items-center justify-center space-x-2 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold px-4 py-2 rounded-lg transition-colors text-sm"
            >
              <MessageSquare className="h-4 w-4" />
              <span>Live Chat</span>
            </Link>
            <Link
              to="/contact"
              className="flex items-center justify-center space-x-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg transition-colors text-sm"
            >
              <Send className="h-4 w-4" />
              <span>Contact Form</span>
            </Link>
            <a
              href="mailto:admin@cyberguardglobal.com"
              className="flex items-center justify-center space-x-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg transition-colors text-sm"
            >
              <Mail className="h-4 w-4" />
              <span>Email Legal Team</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const DisclaimerPage = () => {
  const navigate = useNavigate();
  
  return (
    <LegalPageLayout title="Disclaimer" onBack={() => navigate('/')}>
      <div className="space-y-6 text-gray-300">
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
          <h2 className="text-xl font-bold text-red-400 mb-4">IMPORTANT SECURITY NOTICE</h2>
          <h3 className="text-lg font-semibold text-white mb-3">NO GUARANTEE OF COMPLETE PROTECTION</h3>
          <p>CyberGuard Global AI provides advanced cybersecurity tools, but <strong className="text-red-400">NO SECURITY SOLUTION CAN GUARANTEE 100% PROTECTION</strong> against all cyber threats. Users must understand and accept the following limitations:</p>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">SCANNING LIMITATIONS:</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Multi-engine scanning may not detect zero-day or highly sophisticated malware</li>
            <li>False positives and false negatives can occur</li>
            <li>Scan results depend on third-party database accuracy and coverage</li>
            <li>New threats may not be immediately detectable</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">PRIVACY CHECKUP:</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Privacy Checkup is a guided checklist of your device's own privacy settings. It does not scan your device</li>
            <li>iOS does not allow any app to detect spyware or monitoring software on an iPhone</li>
            <li>Tips for checking a room for hidden cameras will not find every device</li>
            <li>If you believe you are being monitored or are in danger, contact law enforcement or a qualified professional</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">SCAM CHECK:</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Scam Check looks for common warning signs and cannot determine whether a message is a scam</li>
            <li>A message with no warning signs may still be fraudulent</li>
            <li>CyberGuard does not detect deepfakes or AI-cloned voices</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">USER RESPONSIBILITY:</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>MAINTAIN MULTIPLE SECURITY LAYERS:</strong> Use our service alongside other security measures</li>
            <li><strong>KEEP SYSTEMS UPDATED:</strong> Regularly update operating systems and software</li>
            <li><strong>PRACTICE SAFE COMPUTING:</strong> Exercise caution with downloads, emails, and websites</li>
            <li><strong>BACKUP DATA REGULARLY:</strong> Maintain secure backups of important information</li>
            <li><strong>SEEK PROFESSIONAL HELP:</strong> Consult cybersecurity professionals for critical situations</li>
          </ul>
        </div>

        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-400 mb-3">BY USING CYBERGUARD GLOBAL AI, YOU ACKNOWLEDGE THESE LIMITATIONS AND ACCEPT FULL RESPONSIBILITY FOR YOUR CYBERSECURITY.</h3>
          <p>For questions about these disclaimers, contact: <a href="mailto:team@cyberguardglobal.com" className="text-cyan-400 hover:underline">team@cyberguardglobal.com</a></p>
        </div>
      </div>
    </LegalPageLayout>
  );
};

export const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <LegalPageLayout title="Privacy Policy" onBack={() => navigate('/')}>
      <div className="space-y-6 text-gray-300">
        <p className="text-sm text-gray-400">Last Updated: October 26, 2026</p>
        <p className="text-sm text-gray-400">Effective Date: October 26, 2026</p>

        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <p className="text-blue-300">This Privacy Policy applies to the CyberGuard Global website (cyberguardglobal.com), mobile applications (iOS and Android), browser extensions, and all related services (collectively, the "Service").</p>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">1. INFORMATION WE COLLECT</h3>

          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-white mb-2">1.1 Account Information</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Name and email address</li>
                <li>Password (encrypted and hashed)</li>
                <li>Billing and payment information</li>
                <li>Subscription plan and license details</li>
                <li>Account preferences and notification settings</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-2">1.2 Device Information</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Device type, model, and operating system version</li>
                <li>App-generated identifiers for your account and push notifications (we do not collect the advertising identifier, IDFA, and do not track you across other companies' apps or websites)</li>
                <li>IP address and approximate location (city/country level)</li>
                <li>Browser type and version (web platform)</li>
                <li>Mobile carrier information</li>
                <li>App version and build number</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-2">1.3 Usage Data</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Scan results, threat detections, and security analysis</li>
                <li>Files, URLs, email addresses, and IP addresses submitted for scanning</li>
                <li>Feature usage patterns and interaction data</li>
                <li>Error logs and crash reports</li>
                <li>Session duration and frequency of use</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-2">1.4 Email Account Access (Email Protection)</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>If you connect Gmail, Outlook or Yahoo, we receive read-only access to your messages so we can check links and attachments for known threats.</li>
                <li>We never send, delete or move email, and we store only what's needed to show you flagged messages (sender, subject, date and the reason it was flagged).</li>
                <li>You can disconnect at any time in the app or dashboard, or revoke access in your email provider's account settings.</li>
                <li className="text-green-400">CyberGuard's use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" className="underline" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-2">1.5 Messages You Check With Scam Check</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>In the iPhone app and web dashboard, the text you paste into Scam Check is analyzed on your device and is not sent to us or stored. Only links found in the message are sent to our servers to be checked.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-2">1.6 Push Notification Tokens</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Firebase Cloud Messaging tokens for Android</li>
                <li>Apple Push Notification service tokens for iOS</li>
                <li>Used exclusively for security alerts and service notifications</li>
              </ul>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">2. HOW WE USE YOUR INFORMATION</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Service Delivery:</strong> Provide cybersecurity scanning, threat detection, and protection services</li>
            <li><strong>Account Management:</strong> Process payments, manage subscriptions, and verify licenses</li>
            <li><strong>Security Alerts:</strong> Send real-time notifications about detected threats and security events</li>
            <li><strong>Service Improvement:</strong> Analyze usage patterns to improve features and user experience</li>
            <li><strong>Customer Support:</strong> Respond to inquiries and provide technical assistance</li>
            <li><strong>Legal Compliance:</strong> Meet regulatory requirements and respond to legal requests</li>
            <li><strong>Fraud Prevention:</strong> Detect and prevent unauthorized access and abuse</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">3. INFORMATION SHARING</h3>
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-4">
            <p className="text-green-400 font-semibold">We DO NOT sell, rent, or lease your personal information to third parties.</p>
          </div>
          <p className="mb-3">We may share information with:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Security Analysis Partners:</strong> VirusTotal, Google Safe Browsing, PhishTank and AbuseIPDB, to check the files, links and IP addresses you submit. <strong className="text-white">Important:</strong> files VirusTotal hasn't seen before are uploaded to VirusTotal, which shares submitted files with its security community and partners. Don't submit private or confidential files.</li>
            <li><strong>Chat Assistant:</strong> Messages you send to the in-app chat assistant are processed by Anthropic (the provider of the Claude AI model) to generate replies. The assistant is automated and is not a person.</li>
            <li><strong>SMS Alerts:</strong> If you opt in to text alerts, your phone number is shared with Twilio to deliver them. Mobile numbers are never sold or shared for marketing.</li>
            <li><strong>Payments:</strong> In-app subscriptions are processed by Apple. Website purchases are processed by Square or PayPal (we do not store full card numbers).</li>
            <li><strong>Cloud Infrastructure:</strong> MongoDB Atlas, Render, and Netlify for hosting and data storage</li>
            <li><strong>Analytics Services:</strong> To understand app usage and improve performance</li>
            <li><strong>Email Services:</strong> SendGrid for transactional emails and security notifications</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">4. DATA SECURITY</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>All data transmitted between your device and our servers is encrypted using TLS 1.3</li>
            <li>Passwords are hashed using bcrypt with salt</li>
            <li>Sensitive data at rest is encrypted using AES-256 encryption</li>
            <li>Authentication uses JWT tokens with automatic expiration</li>
            <li>Access to production systems is restricted and monitored</li>
            <li>Regular security audits and penetration testing</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">5. DATA RETENTION</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account Data:</strong> Retained while your account is active and for 30 days after deletion request</li>
            <li><strong>Scan History:</strong> Retained for 90 days, then automatically deleted</li>
            <li><strong>Usage Analytics:</strong> Aggregated and anonymized after 12 months</li>
            <li><strong>Security Logs:</strong> Retained for 1 year for security investigation purposes</li>
            <li><strong>Payment Records:</strong> Retained as required by tax and financial regulations (typically 7 years)</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">6. YOUR RIGHTS</h3>
          <p className="mb-3">You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
            <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            <li><strong>Restrict Processing:</strong> Limit how we use your data in certain circumstances</li>
            <li><strong>Withdraw Consent:</strong> Revoke previously given consent at any time</li>
          </ul>
          <p className="mt-3 text-sm">To exercise these rights, contact us at <a href="mailto:privacy@cyberguardglobal.com" className="text-cyan-400 hover:underline">privacy@cyberguardglobal.com</a> or use the in-app settings.</p>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">7. CHILDREN'S PRIVACY</h3>
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
            <p className="text-yellow-300 font-semibold mb-2">COPPA COMPLIANCE</p>
            <p>Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately at <a href="mailto:privacy@cyberguardglobal.com" className="text-cyan-400 hover:underline">privacy@cyberguardglobal.com</a>. We will promptly delete such information from our systems.</p>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">8. CALIFORNIA RESIDENTS (CCPA/CPRA)</h3>
          <p className="mb-3">If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Right to Know:</strong> Request disclosure of personal information collected, used, disclosed, or sold</li>
            <li><strong>Right to Delete:</strong> Request deletion of personal information (with some exceptions)</li>
            <li><strong>Right to Opt-Out:</strong> Opt-out of the sale or sharing of personal information</li>
            <li><strong>Right to Non-Discrimination:</strong> Exercise your privacy rights without discriminatory treatment</li>
            <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
            <li><strong>Right to Limit:</strong> Limit the use and disclosure of sensitive personal information</li>
          </ul>
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mt-3">
            <p className="text-green-400">We do not sell personal information as defined under CCPA/CPRA.</p>
          </div>
          <p className="mt-3 text-sm">To submit a verifiable consumer request, email <a href="mailto:privacy@cyberguardglobal.com" className="text-cyan-400 hover:underline">privacy@cyberguardglobal.com</a> with subject line "CCPA Request".</p>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">9. EUROPEAN USERS (GDPR)</h3>
          <p className="mb-3">If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Legal Basis:</strong> We process your data based on consent, contract performance, legitimate interests, or legal obligations</li>
            <li><strong>Data Transfers:</strong> Your data may be transferred to the United States where our servers are located. We use Standard Contractual Clauses (SCCs) to ensure adequate protection</li>
            <li><strong>Supervisory Authority:</strong> You have the right to lodge a complaint with your local data protection authority</li>
            <li><strong>Data Protection Officer:</strong> Contact our DPO at <a href="mailto:dpo@cyberguardglobal.com" className="text-cyan-400 hover:underline">dpo@cyberguardglobal.com</a></li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">10. THIRD-PARTY SERVICES AND SDKS</h3>
          <p className="mb-3">Our mobile applications may include the following third-party services:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Apple Push Notification service:</strong> Delivers security alerts to your iPhone</li>
            <li><strong>Apple StoreKit:</strong> Handles in-app subscriptions</li>
            <li><strong>On-device storage:</strong> Keeps your settings and checklist progress on your phone</li>
          </ul>
          <p className="mt-3 text-sm">These services have their own privacy policies. We encourage you to review them.</p>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">11. COOKIES AND TRACKING (WEB PLATFORM)</h3>
          <p className="mb-3">Our web platform uses:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Essential Cookies:</strong> Required for authentication and security features</li>
            <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our platform</li>
          </ul>
          <p className="mt-3 text-sm">You can control cookies through your browser settings. Disabling essential cookies may affect functionality.</p>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">12. CHANGES TO THIS POLICY</h3>
          <p>We may update this Privacy Policy from time to time. We will notify you of material changes by:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>Posting the updated policy on our website and in the app</li>
            <li>Sending an email notification to registered users</li>
            <li>Displaying an in-app notification</li>
          </ul>
          <p className="mt-3">Your continued use of the Service after changes indicates acceptance of the updated policy.</p>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">13. CONTACT US</h3>
          <p className="mb-3">For privacy questions, data requests, or concerns:</p>
          <div className="bg-gray-800/50 rounded-lg p-4 space-y-2">
            <p><strong className="text-white">Email:</strong> <a href="mailto:privacy@cyberguardglobal.com" className="text-cyan-400 hover:underline">privacy@cyberguardglobal.com</a></p>
            <p><strong className="text-white">Data Protection Officer:</strong> <a href="mailto:dpo@cyberguardglobal.com" className="text-cyan-400 hover:underline">dpo@cyberguardglobal.com</a></p>
            <p><strong className="text-white">General Inquiries:</strong> <a href="mailto:admin@cyberguardglobal.com" className="text-cyan-400 hover:underline">admin@cyberguardglobal.com</a></p>
            <p><strong className="text-white">Mailing Address:</strong></p>
            <p className="pl-4 text-gray-400">CyberGuard Global AI<br />Privacy Department<br />United States</p>
          </div>
          <p className="mt-3 text-sm">We aim to respond to all privacy requests within 30 days.</p>
        </div>

        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6">
          <p className="text-blue-400 font-semibold">BY USING CYBERGUARD GLOBAL AI SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO THE COLLECTION AND USE OF YOUR INFORMATION AS DESCRIBED IN THIS PRIVACY POLICY.</p>
        </div>
      </div>
    </LegalPageLayout>
  );
};

export const TermsOfServicePage = () => {
  const navigate = useNavigate();
  
  return (
    <LegalPageLayout title="Terms of Service" onBack={() => navigate('/')}>
      <div className="space-y-6 text-gray-300">
        <p className="text-sm text-gray-400">Last Updated: October 26, 2026</p>
        
        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">1. ACCEPTANCE OF TERMS</h3>
          <p>By accessing or using CyberGuard Global AI services ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.</p>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">2. DESCRIPTION OF SERVICE</h3>
          <p>CyberGuard Global AI provides cybersecurity scanning, threat detection, and security monitoring services through our web-based platform. Our services include but are not limited to:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>File, URL and IP address checks using third-party services</li>
            <li>Email Protection for connected email accounts (read-only)</li>
            <li>Scam Check for messages you paste in</li>
            <li>Privacy Checkup guidance</li>
            <li>Desktop Protection alerts from an agent you install on your own computer</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">3. DISCLAIMER OF WARRANTIES</h3>
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p className="text-red-400 font-semibold">IMPORTANT: THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.</p>
          </div>
          <ul className="list-disc pl-6 space-y-2">
            <li>We do not guarantee that our Service will detect all threats, malware, or security vulnerabilities</li>
            <li>Security scanning results are for informational purposes only</li>
            <li>No cybersecurity solution can provide 100% protection</li>
            <li>Third-party results (VirusTotal, Google Safe Browsing, PhishTank, AbuseIPDB) are subject to their limitations</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">4. LIMITATION OF LIABILITY</h3>
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-4">
            <p className="text-yellow-400 font-semibold">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
          </div>
          <ul className="list-disc pl-6 space-y-2">
            <li>CyberGuard Global AI SHALL NOT BE LIABLE for any direct, indirect, incidental, special, consequential, or punitive damages</li>
            <li>This includes but is not limited to: data loss, system damage, financial losses, business interruption, or privacy breaches</li>
            <li>Our total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">5. USER RESPONSIBILITIES</h3>
          <p>You acknowledge and agree that:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>You are solely responsible for your cybersecurity practices</li>
            <li>Our Service supplements but does not replace comprehensive security measures</li>
            <li>You should maintain backups and multiple layers of security</li>
            <li>You will not rely solely on our Service for protection</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">6. SUBSCRIPTION AND BILLING</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Subscriptions automatically renew monthly unless cancelled</li>
            <li>All fees are non-refundable except as required by law</li>
            <li>We offer a 30-day money-back guarantee for first-time subscribers</li>
            <li>Price changes will be communicated 30 days in advance</li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-cyan-400 mb-3">13. CONTACT INFORMATION</h3>
          <p>For questions about these Terms, contact us at:</p>
          <ul className="mt-2 space-y-1">
            <li>Email: <a href="mailto:admin@cyberguardglobal.com" className="text-cyan-400 hover:underline">admin@cyberguardglobal.com</a></li>
          </ul>
        </div>

        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6">
          <p className="text-blue-400 font-semibold">BY USING CYBERGUARD GLOBAL AI, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.</p>
        </div>
      </div>
    </LegalPageLayout>
  );
};